/**
 * games/$gameSlug.tsx — Game detail / host route.
 *
 * Lives at `/games/$gameSlug`. Reads catalog + state + session, resolves
 * the slug, and delegates to <GameDetailSurface> for rendering. The
 * surface itself handles the 4 variants (classroom-locked / guest / login
 * disabled / login enabled) so this route stays thin.
 */

import type { ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { GameDetailSurface } from '../../components/public/GameDetailSurface.js';
import { TutorGuidePanel } from '../../components/tutor/TutorGuidePanel.js';
import {
  publicCatalogQuery,
  catalogStateQuery,
  sessionQuery,
  myClassContentQuery,
  mergeCatalogWithState,
  findGameBySlug,
} from '../../lib/api/queries.js';

export const Route = createFileRoute('/games/$gameSlug')({
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(publicCatalogQuery);
    return null;
  },
  component: GameHostPage,
});

function GameHostPage(): ReactNode {
  const { gameSlug } = Route.useParams();
  const catalog = useQuery(publicCatalogQuery);
  const state = useQuery(catalogStateQuery);
  const session = useQuery(sessionQuery);
  const sessionLoggedIn = !!(session.data?.ok && session.data.data?.user);
  const classContent = useQuery({ ...myClassContentQuery, enabled: sessionLoggedIn });

  if (catalog.isLoading) {
    return (
      <main className="page-shell">
        <p className="form-message">Memuat katalog…</p>
      </main>
    );
  }

  const stateMap = state.data?.ok ? (state.data.data?.games ?? {}) : {};
  const games = mergeCatalogWithState(catalog.data?.games ?? [], stateMap, 'gameId');
  const game = findGameBySlug(games, gameSlug);

  if (!game) {
    return (
      <main className="page-shell">
        <PageHeader
          eyebrow="Game"
          title="Game tidak ditemukan"
          description={`Kode '${gameSlug}' tidak cocok dengan game apa pun di katalog.`}
        />
        <section className="section">
          <StatePanel
            variant="error"
            title="Slug tidak dikenal"
            description="Pilih game dari katalog publik atau kembali ke dasbor kamu."
            action={
              <Link className="button button-primary" to="/games">
                Buka katalog
              </Link>
            }
          />
        </section>
      </main>
    );
  }

  const isLoggedIn = !!(session.data?.ok && session.data.data?.user);

  // F-2: games the tutor opened for the student's class are playable.
  const classOpened =
    isLoggedIn &&
    (classContent.data?.classes ?? []).some((c) => c.games.some((g) => g.gameId === game.gameId));

  // "Panduan Tutor" — renders nothing (and requests nothing) unless the viewer
  // is an approved tutor or admin; the API enforces the same rule (401/403).
  return (
    <>
      <GameDetailSurface game={game} isLoggedIn={isLoggedIn} classOpened={classOpened} />
      <div className="page-shell tutor-guide-host">
        <TutorGuidePanel gameId={game.gameId} gameTitle={game.title} />
      </div>
    </>
  );
}